import { createApiClient } from "./client";

export type TicketStatus =
  | "Unread"
  | "Open"
  | "With User"
  | "In Progress"
  | "Pending"
  | "Resolved"
  | "Closed";

export type TicketPriority = "Low" | "Medium" | "High" | "Critical";

export interface AssignedTo {
  id: string;
  name: string;
  email: string;
}

export interface Ticket {
  id: string;
  ticketNumber: number;
  subject: string;
  bodyPreview?: string;
  status: TicketStatus;
  priority: TicketPriority;
  assignedToId: string | null;
  assignedTo?: AssignedTo | null;
  fromName?: string;
  fromEmail?: string;
  teamId?: string | null;
  tags?: string[];
  createdAt: string;
  updatedAt: string;
}

export interface Comment {
  id: string;
  ticketId: string;
  agentId: string | null;
  authorName?: string;
  authorEmail?: string;
  content: string;
  contentType: "text" | "html";
  isInternal: boolean;
  isSentViaEmail?: boolean;
  createdAt: string;
  attachments?: { id: string; filename: string; url?: string }[];
}

export interface TicketListResponse {
  tickets: Ticket[];
  pagination: { page: number; limit: number; total: number; pages: number };
}

export interface TicketDetailResponse extends Ticket {
  body?: { content: string; contentType: string } | null;
}

export interface ListTicketsParams {
  status?: TicketStatus;
  assignedToId?: string;
  unassigned?: boolean;
  page?: number;
  limit?: number;
  teamId?: string;
  excludeClosedResolved?: boolean;
}

export function createTicketsApi(token: string, workspaceId: string | null) {
  const client = createApiClient(token, workspaceId);

  return {
    list(params: ListTicketsParams) {
      const qs = new URLSearchParams();
      if (params.status) qs.set("status", params.status);
      if (params.assignedToId) qs.set("assignedToId", params.assignedToId);
      if (params.unassigned) qs.set("unassigned", "true");
      if (params.page) qs.set("page", String(params.page));
      qs.set("limit", String(params.limit ?? 50));
      if (params.teamId) qs.set("teamId", params.teamId);
      if (params.excludeClosedResolved) qs.set("excludeClosedResolved", "true");
      return client.get<TicketListResponse>(`/tickets?${qs}`);
    },

    get(id: string) {
      return client.get<TicketDetailResponse>(`/tickets/${id}`);
    },

    comments(ticketId: string) {
      return client.get<{ comments: Comment[] }>(
        `/comments?ticketId=${encodeURIComponent(ticketId)}`
      );
    },

    updateStatus(id: string, status: TicketStatus) {
      return client.patch<Ticket>(`/tickets/${id}`, { status });
    },

    assign(id: string, agentId: string | null) {
      return client.patch<Ticket>(`/tickets/${id}`, { assignedToId: agentId });
    },

    // Public reply (sends email to contact via Graph) when isInternal=false.
    // Internal note (no email) when isInternal=true. Same endpoint handles both
    // and matches the SaaS web app behavior.
    addComment(
      id: string,
      body: { content: string; isInternal: boolean; contentType?: "text" | "html" }
    ) {
      return client.post<Comment>(`/tickets/${id}/reply`, {
        content: body.content,
        contentType: body.contentType ?? "text",
        isInternal: body.isInternal,
      });
    },
  };
}
